/**
 * BrokerageContractUploadWizard
 *
 * Salvaged from origin/feat/brokerage-contracts (PR #1344) and re-homed onto
 * the NestJS API built in Inc-1/2.
 *
 * Entry modes (`initialMode`, default "choose"):
 *   • "choose"  → a small chooser lets the user pick how to start.
 *   • "upload"  → Upload → Extracting → Review → Link → Confirm (auto-extract).
 *   • "manual"  → Review → Link → Confirm (skip OCR, fill the fields by hand).
 * Manual entry drops the Upload/Extracting steps; the create flow is identical
 * (no file means uploadDocument is simply skipped — it is already null-safe).
 *
 * On confirm, the wizard orchestrates the repository in this order:
 *   1. create()         — POST /brokerage-contracts (JSON, owner_id required)
 *   2. uploadDocument() — POST /brokerage-contracts/:id/document (multipart)
 *   3. linkProperty()   — POST /brokerage-contracts/:id/links (JSON) — only when
 *                         both a property AND a contract_type are selected
 *
 * Inc-8: widened the Review step to show/edit the FULL extracted field set for
 * BOTH parties + property (broker/owner/property/contract meta). The full
 * extracted set is always passed to extracted_data_json so nothing is lost.
 *
 * Changes vs #1344:
 * - Dropped `useCreateBrokerageContract` (Supabase hook). Orchestrates the
 *   repository directly with a local `useState`-based pending flag.
 * - owner_id is REQUIRED — Next/Save is blocked until an owner is selected.
 * - Added `contract_type` Select in the Link step (marketing|sales|management).
 * - property_id and company_id removed from the create payload.
 * - RTL icon fix: single icon + rtl:rotate-180, no `language` conditional.
 * - `language` removed from useLanguage() destructure (not needed after fix).
 */
import { useState, useRef, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { DatePicker } from "@/components/ui/date-picker";
import { useBrokerageExtraction } from "@/hooks/useBrokerageExtraction";
import { useOwners } from "@/hooks/useOwners";
import { useProperties } from "@/hooks/useProperties";
import { BrokerageOwnerResolver } from "@/components/brokerage/BrokerageOwnerResolver";
import { BrokeragePropertyResolver } from "@/components/brokerage/BrokeragePropertyResolver";
import { brokerageContractsRepository } from "@/repositories/brokerageContracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/auth/PhoneInput";
import { toE164Phone } from "@/lib/phone";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2,
  Upload,
  FileText,
  Pencil,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { ExtractedBrokerageFields } from "@/types/brokerage";
import {
  MAX_BROKERAGE_PDF_BYTES,
  MAX_BROKERAGE_PDF_MB,
  type BrokerageOpsType,
  type Json,
} from "@dallali/shared";

interface BrokerageContractUploadWizardProps {
  onSuccess?: (contractId: string) => void;
  onCancel?: () => void;
  /** Pre-fill owner selection */
  initialOwnerId?: string;
  /** Pre-fill property selection */
  initialPropertyId?: string;
  /**
   * How the wizard starts:
   * - "choose" (default): show the upload-vs-manual chooser first.
   * - "upload": jump straight to the PDF upload step.
   * - "manual": skip OCR and open the review form with empty fields.
   */
  initialMode?: "choose" | "upload" | "manual";
}

type WizardStep = "choose" | "upload" | "extracting" | "review" | "link" | "confirm";
type EntryMode = "upload" | "manual";

const CONTRACT_TYPE_OPTIONS: { value: BrokerageOpsType; labelAr: string; labelEn: string }[] = [
  { value: "marketing", labelAr: "تسويق", labelEn: "Marketing" },
  { value: "sales", labelAr: "بيع", labelEn: "Sales" },
  { value: "management", labelAr: "إدارة", labelEn: "Management" },
];

function StepIndicator({ step, current }: { step: number; current: number }) {
  const done = step < current;
  const active = step === current;
  return (
    <div
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold",
        done && "border-primary bg-primary text-primary-foreground",
        active && "border-primary text-primary",
        !done && !active && "border-muted text-muted-foreground",
      )}
    >
      {done ? <CheckCircle2 className="h-4 w-4" /> : step}
    </div>
  );
}

export function BrokerageContractUploadWizard({
  onSuccess,
  onCancel,
  initialOwnerId = "",
  initialPropertyId = "",
  initialMode = "choose",
}: BrokerageContractUploadWizardProps) {
  const { t } = useLanguage();
  const { extract, state: extractionState } = useBrokerageExtraction();
  const { data: owners = [] } = useOwners();
  const { data: properties = [] } = useProperties();

  const [step, setStep] = useState<WizardStep>(
    initialMode === "upload"
      ? "upload"
      : initialMode === "manual"
        ? "review"
        : "choose",
  );
  // Drives the step indicator + confirm-step copy. Manual entry has no Upload step.
  const [mode, setMode] = useState<EntryMode>(
    initialMode === "manual" ? "manual" : "upload",
  );
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extracted fields (editable) + frozen snapshot of the raw extractor output
  // for the brokerage_contracts.extracted_data_json audit column.
  const [fields, setFields] = useState<ExtractedBrokerageFields>({});
  const [originalExtracted, setOriginalExtracted] =
    useState<ExtractedBrokerageFields | null>(null);
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());

  // Link step
  const [selectedOwnerId, setSelectedOwnerId] = useState(initialOwnerId);
  const [selectedPropertyId, setSelectedPropertyId] = useState(initialPropertyId);
  const [selectedContractType, setSelectedContractType] =
    useState<BrokerageOpsType>("marketing");
  // AC2 — unit scope: whole property (allUnits) or specific unit ids.
  const [allUnits, setAllUnits] = useState(true);
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);

  // Confirm step
  const [acknowledged, setAcknowledged] = useState(false);

  const validateFile = useCallback(
    (f: File): string | null => {
      if (f.type !== "application/pdf") {
        return t("يجب أن يكون الملف بصيغة PDF", "File must be a PDF");
      }
      if (f.size > MAX_BROKERAGE_PDF_BYTES) {
        return t(
          `حجم الملف لا يجب أن يتجاوز ${MAX_BROKERAGE_PDF_MB} ميغابايت`,
          `File size must not exceed ${MAX_BROKERAGE_PDF_MB} MB`,
        );
      }
      return null;
    },
    [t],
  );

  const handleFileSelected = useCallback(
    async (f: File) => {
      const error = validateFile(f);
      if (error) {
        setFileError(error);
        return;
      }
      setFileError(null);
      setFile(f);
      setStep("extracting");

      const result = await extract(f);
      if (result) {
        setOriginalExtracted(result);
        setFields(result);
        setStep("review");
      } else {
        // Extraction failed — still allow manual entry
        setOriginalExtracted(null);
        setFields({});
        setStep("review");
      }
    },
    [extract, validateFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFileSelected(dropped);
    },
    [handleFileSelected],
  );

  const handleFieldChange = (
    key: string,
    value: string | number | null,
  ) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setEditedFields((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  // Manual entry: skip OCR entirely and open the review form with empty fields.
  const startManual = useCallback(() => {
    setMode("manual");
    setFile(null);
    setOriginalExtracted(null);
    setFields({});
    setEditedFields(new Set());
    setStep("review");
  }, []);

  const handleSubmit = async () => {
    if (!acknowledged || !selectedOwnerId || isSubmitting) return;

    if (!fields.rega_contract_number || !fields.start_date || !fields.end_date) {
      toast.error(
        t(
          "يجب تعبئة رقم العقد وتاريخي البداية والنهاية قبل الحفظ",
          "REGA contract number, start date, and end date are required before saving",
        ),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // Step 1: create the contract row (owner_id required; no company_id, no property_id).
      // All Inc-8 fields (broker contact/email/address, owner contact/email/address/poa,
      // property registration/address/type/use/plan/plot/area/prices/services) are
      // passed through so nothing extracted is lost.
      const contract = await brokerageContractsRepository.create({
        owner_id: selectedOwnerId,
        rega_contract_number: fields.rega_contract_number,
        start_date: fields.start_date,
        end_date: fields.end_date,
        // Contract meta
        contract_execution_date: fields.contract_execution_date ?? undefined,
        place_city: fields.place_city ?? undefined,
        activity_type: fields.activity_type ?? undefined,
        deed_number: fields.deed_number ?? undefined,
        duration_text: fields.duration_text ?? undefined,
        // Broker (party 1)
        broker_name: fields.broker_name ?? undefined,
        broker_license_number: fields.broker_license_number ?? undefined,
        broker_type: fields.broker_type ?? undefined,
        broker_id_number: fields.broker_id_number ?? undefined,
        broker_id_type: fields.broker_id_type ?? undefined,
        broker_contact_no: fields.broker_contact_no ?? undefined,
        broker_email: fields.broker_email ?? undefined,
        broker_national_address: fields.broker_national_address ?? undefined,
        broker_iban: fields.broker_iban ?? undefined,
        broker_bank_name: fields.broker_bank_name ?? undefined,
        // Owner (party 2) + representative
        owner_name_on_contract: fields.owner_name_on_contract ?? undefined,
        owner_id_number: fields.owner_id_number ?? undefined,
        owner_id_type: fields.owner_id_type ?? undefined,
        owner_contact_no: fields.owner_contact_no ?? undefined,
        owner_email: fields.owner_email ?? undefined,
        owner_national_address: fields.owner_national_address ?? undefined,
        owner_representative_name: fields.owner_representative_name ?? undefined,
        owner_representative_id: fields.owner_representative_id ?? undefined,
        owner_representative_mobile: fields.owner_representative_mobile ?? undefined,
        owner_representative_poa_no: fields.owner_representative_poa_no ?? undefined,
        // Property (Inc-8)
        property_registration_no: fields.property_registration_no ?? undefined,
        deed_date: fields.deed_date ?? undefined,
        property_address: fields.property_address ?? undefined,
        property_type: fields.property_type ?? undefined,
        property_use: fields.property_use ?? undefined,
        plan_no: fields.plan_no ?? undefined,
        plot_no: fields.plot_no ?? undefined,
        property_area: fields.property_area ?? undefined,
        min_unit_sale_price: fields.min_unit_sale_price ?? undefined,
        min_annual_rent: fields.min_annual_rent ?? undefined,
        property_services: fields.property_services ?? undefined,
        // Commission + financials
        sales_commission_pct: fields.sales_commission_pct ?? undefined,
        marketing_commission_pct: fields.marketing_commission_pct ?? undefined,
        payment_method: fields.payment_method ?? undefined,
        notes: fields.notes ?? undefined,
        status: "pending",
        manually_edited_fields: Array.from(editedFields),
        extracted_data_json:
          originalExtracted != null
            ? (originalExtracted as unknown as Json)
            : undefined,
      });

      // Step 2: upload the PDF so document_url is set on the contract row.
      // Best-effort: a storage failure must NOT lose the just-created contract —
      // surface a clear warning and let the user re-attach later (the document
      // endpoint itself is unit-tested in the BE; in prod storage is healthy).
      if (file) {
        try {
          await brokerageContractsRepository.uploadDocument(contract.id, file);
        } catch {
          toast.warning(
            t(
              "تم إنشاء العقد، لكن تعذّر إرفاق ملف PDF — يمكنك إرفاقه لاحقًا",
              "Contract created, but attaching the PDF failed — you can attach it later",
            ),
          );
        }
      }

      // Step 3: link property via junction (only when both property and op are chosen)
      // AC2 — scope the link to the whole property (all_units) or to the chosen
      // specific units (one junction row per unit).
      if (selectedPropertyId) {
        await brokerageContractsRepository.linkProperty(contract.id, {
          property_id: selectedPropertyId,
          contract_type: selectedContractType,
          ...(allUnits
            ? { all_units: true }
            : selectedUnitIds.length > 0
              ? { unit_ids: selectedUnitIds }
              : {}),
        });
      }

      toast.success(t("تم إنشاء عقد الوساطة بنجاح", "Brokerage contract created successfully"));
      onSuccess?.(contract.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("حدث خطأ", "An error occurred");
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step: choose ──────────────────────────────────────────────────────────
  // Entry chooser: upload a PDF (auto-extract) or enter the fields by hand.
  if (step === "choose") {
    return (
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          {t(
            "كيف تريد إضافة عقد الوساطة؟",
            "How would you like to add the brokerage contract?",
          )}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ModeCard
            icon={<Upload className="h-8 w-8" />}
            title={t("رفع ملف PDF", "Upload PDF")}
            description={t(
              "استخراج بيانات العقد تلقائياً من ملف PDF",
              "Auto-extract the contract fields from a PDF",
            )}
            onClick={() => {
              setMode("upload");
              setStep("upload");
            }}
          />
          <ModeCard
            icon={<Pencil className="h-8 w-8" />}
            title={t("إدخال يدوي", "Manual entry")}
            description={t(
              "تعبئة بيانات العقد يدوياً دون رفع ملف",
              "Fill in the contract fields by hand — no file needed",
            )}
            onClick={startManual}
          />
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onCancel}>
            {t("إلغاء", "Cancel")}
          </Button>
        </div>
      </div>
    );
  }

  // ── Step: upload ──────────────────────────────────────────────────────────
  if (step === "upload") {
    return (
      <div className="space-y-6">
        <StepsHeader current="upload" mode={mode} />
        <div
          role="button"
          tabIndex={0}
          aria-label={t("منطقة رفع الملف", "File upload area")}
          className={cn(
            "flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 text-center transition-colors cursor-pointer",
            dragOver
              ? "border-primary bg-primary/5"
              : "border-muted hover:border-muted-foreground/50",
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
          }}
        >
          <Upload className="h-10 w-10 text-muted-foreground mb-4" />
          <p className="font-semibold text-lg">
            {t("اسحب ملف PDF هنا أو انقر للاختيار", "Drag a PDF here or click to select")}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              `الحد الأقصى ${MAX_BROKERAGE_PDF_MB} ميغابايت`,
              `Maximum ${MAX_BROKERAGE_PDF_MB} MB`,
            )}
          </p>
          {fileError && (
            <p className="text-sm text-destructive mt-3 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> {fileError}
            </p>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFileSelected(f);
          }}
        />
        <div className="flex justify-end gap-2">
          {initialMode !== "upload" && (
            <Button
              variant="outline"
              className="me-auto"
              onClick={() => setStep("choose")}
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
              {t("رجوع", "Back")}
            </Button>
          )}
          <Button variant="outline" onClick={onCancel}>
            {t("إلغاء", "Cancel")}
          </Button>
        </div>
      </div>
    );
  }

  // ── Step: extracting ──────────────────────────────────────────────────────
  if (step === "extracting") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-lg font-medium">
          {t("جارٍ استخراج البيانات...", "Extracting fields...")}
        </p>
        <p className="text-sm text-muted-foreground">{file?.name}</p>
      </div>
    );
  }

  // ── Step: review ──────────────────────────────────────────────────────────
  // Shows the FULL extracted field set grouped by: Contract / Broker / Owner / Property.
  // All fields are editable (changes are tracked in manually_edited_fields).
  if (step === "review") {
    return (
      <div className="space-y-4">
        <StepsHeader current="review" mode={mode} />
        {extractionState.error && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              {t(
                "فشل الاستخراج التلقائي. يمكنك الإدخال اليدوي:",
                "Auto-extraction failed. Manual entry:",
              )}
            </span>
          </div>
        )}

        {file && (
          <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-3 text-sm">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{file.name}</span>
          </div>
        )}

        {/* ── Contract meta ── */}
        <SectionLabel label={t("بيانات العقد", "Contract Details")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReviewField
            label={t("رقم عقد REGA", "REGA Contract #")}
            fieldKey="rega_contract_number"
            value={fields.rega_contract_number ?? ""}
            edited={editedFields.has("rega_contract_number")}
            onChange={(v) => handleFieldChange("rega_contract_number", v || null)}
          />
          <ReviewField
            label={t("تاريخ تنفيذ العقد", "Contract Execution Date")}
            fieldKey="contract_execution_date"
            value={fields.contract_execution_date ?? ""}
            edited={editedFields.has("contract_execution_date")}
            onChange={(v) => handleFieldChange("contract_execution_date", v || null)}
            type="date"
          />
          <ReviewField
            label={t("مدينة التوقيع", "Signing City")}
            fieldKey="place_city"
            value={fields.place_city ?? ""}
            edited={editedFields.has("place_city")}
            onChange={(v) => handleFieldChange("place_city", v || null)}
          />
          <ReviewField
            label={t("نوع النشاط", "Activity Type")}
            fieldKey="activity_type"
            value={fields.activity_type ?? ""}
            edited={editedFields.has("activity_type")}
            onChange={(v) => handleFieldChange("activity_type", v || null)}
          />
          <ReviewField
            label={t("تاريخ البداية", "Start Date")}
            fieldKey="start_date"
            value={fields.start_date ?? ""}
            edited={editedFields.has("start_date")}
            onChange={(v) => handleFieldChange("start_date", v || null)}
            type="date"
          />
          <ReviewField
            label={t("تاريخ الانتهاء", "End Date")}
            fieldKey="end_date"
            value={fields.end_date ?? ""}
            edited={editedFields.has("end_date")}
            onChange={(v) => handleFieldChange("end_date", v || null)}
            type="date"
          />
          <ReviewField
            label={t("مدة العقد", "Duration")}
            fieldKey="duration_text"
            value={fields.duration_text ?? ""}
            edited={editedFields.has("duration_text")}
            onChange={(v) => handleFieldChange("duration_text", v || null)}
          />
        </div>

        {/* ── Broker (party 1 / الوسيط) ── */}
        <SectionLabel label={t("الطرف الأول — الوسيط", "First Party — Broker")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReviewField
            label={t("اسم الوسيط", "Broker Name")}
            fieldKey="broker_name"
            value={fields.broker_name ?? ""}
            edited={editedFields.has("broker_name")}
            onChange={(v) => handleFieldChange("broker_name", v || null)}
          />
          <ReviewField
            label={t("نوع الوسيط", "Broker Type")}
            fieldKey="broker_type"
            value={fields.broker_type ?? ""}
            edited={editedFields.has("broker_type")}
            onChange={(v) => handleFieldChange("broker_type", v || null)}
          />
          <ReviewField
            label={t("رقم هوية الوسيط", "Broker ID #")}
            fieldKey="broker_id_number"
            value={fields.broker_id_number ?? ""}
            edited={editedFields.has("broker_id_number")}
            onChange={(v) => handleFieldChange("broker_id_number", v || null)}
          />
          <ReviewField
            label={t("نوع هوية الوسيط", "Broker ID Type")}
            fieldKey="broker_id_type"
            value={fields.broker_id_type ?? ""}
            edited={editedFields.has("broker_id_type")}
            onChange={(v) => handleFieldChange("broker_id_type", v || null)}
          />
          <ReviewField
            label={t("رقم ترخيص الوسيط", "Broker License #")}
            fieldKey="broker_license_number"
            value={fields.broker_license_number ?? ""}
            edited={editedFields.has("broker_license_number")}
            onChange={(v) => handleFieldChange("broker_license_number", v || null)}
          />
          <ReviewField
            label={t("رقم تواصل الوسيط", "Broker Contact No.")}
            fieldKey="broker_contact_no"
            value={fields.broker_contact_no ?? ""}
            edited={editedFields.has("broker_contact_no")}
            onChange={(v) => handleFieldChange("broker_contact_no", v || null)}
          />
          <ReviewField
            label={t("بريد الوسيط الإلكتروني", "Broker Email")}
            fieldKey="broker_email"
            value={fields.broker_email ?? ""}
            edited={editedFields.has("broker_email")}
            onChange={(v) => handleFieldChange("broker_email", v || null)}
          />
          <ReviewField
            label={t("العنوان الوطني للوسيط", "Broker National Address")}
            fieldKey="broker_national_address"
            value={fields.broker_national_address ?? ""}
            edited={editedFields.has("broker_national_address")}
            onChange={(v) => handleFieldChange("broker_national_address", v || null)}
          />
          <ReviewField
            label={t("IBAN الوسيط", "Broker IBAN")}
            fieldKey="broker_iban"
            value={fields.broker_iban ?? ""}
            edited={editedFields.has("broker_iban")}
            onChange={(v) => handleFieldChange("broker_iban", v || null)}
          />
          <ReviewField
            label={t("اسم البنك", "Bank Name")}
            fieldKey="broker_bank_name"
            value={fields.broker_bank_name ?? ""}
            edited={editedFields.has("broker_bank_name")}
            onChange={(v) => handleFieldChange("broker_bank_name", v || null)}
          />
        </div>

        {/* ── Owner (party 2 / المالك) ── */}
        <SectionLabel label={t("الطرف الثاني — المالك", "Second Party — Owner")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReviewField
            label={t("اسم المالك في العقد", "Owner Name on Contract")}
            fieldKey="owner_name_on_contract"
            value={fields.owner_name_on_contract ?? ""}
            edited={editedFields.has("owner_name_on_contract")}
            onChange={(v) => handleFieldChange("owner_name_on_contract", v || null)}
          />
          <ReviewField
            label={t("رقم هوية المالك", "Owner ID #")}
            fieldKey="owner_id_number"
            value={fields.owner_id_number ?? ""}
            edited={editedFields.has("owner_id_number")}
            onChange={(v) => handleFieldChange("owner_id_number", v || null)}
          />
          <ReviewField
            label={t("نوع هوية المالك", "Owner ID Type")}
            fieldKey="owner_id_type"
            value={fields.owner_id_type ?? ""}
            edited={editedFields.has("owner_id_type")}
            onChange={(v) => handleFieldChange("owner_id_type", v || null)}
          />
          <ReviewField
            label={t("رقم تواصل المالك", "Owner Contact No.")}
            fieldKey="owner_contact_no"
            value={fields.owner_contact_no ?? ""}
            edited={editedFields.has("owner_contact_no")}
            onChange={(v) => handleFieldChange("owner_contact_no", v || null)}
            type="phone"
          />
          <ReviewField
            label={t("بريد المالك الإلكتروني", "Owner Email")}
            fieldKey="owner_email"
            value={fields.owner_email ?? ""}
            edited={editedFields.has("owner_email")}
            onChange={(v) => handleFieldChange("owner_email", v || null)}
          />
          <ReviewField
            label={t("العنوان الوطني للمالك", "Owner National Address")}
            fieldKey="owner_national_address"
            value={fields.owner_national_address ?? ""}
            edited={editedFields.has("owner_national_address")}
            onChange={(v) => handleFieldChange("owner_national_address", v || null)}
          />
          <ReviewField
            label={t("اسم الممثل القانوني", "Representative Name")}
            fieldKey="owner_representative_name"
            value={fields.owner_representative_name ?? ""}
            edited={editedFields.has("owner_representative_name")}
            onChange={(v) => handleFieldChange("owner_representative_name", v || null)}
          />
          <ReviewField
            label={t("هوية الممثل", "Representative ID")}
            fieldKey="owner_representative_id"
            value={fields.owner_representative_id ?? ""}
            edited={editedFields.has("owner_representative_id")}
            onChange={(v) => handleFieldChange("owner_representative_id", v || null)}
          />
          <ReviewField
            label={t("هاتف الممثل", "Representative Mobile")}
            fieldKey="owner_representative_mobile"
            value={fields.owner_representative_mobile ?? ""}
            edited={editedFields.has("owner_representative_mobile")}
            onChange={(v) => handleFieldChange("owner_representative_mobile", v || null)}
            type="phone"
          />
          <ReviewField
            label={t("رقم التوكيل (POA)", "POA #")}
            fieldKey="owner_representative_poa_no"
            value={fields.owner_representative_poa_no ?? ""}
            edited={editedFields.has("owner_representative_poa_no")}
            onChange={(v) => handleFieldChange("owner_representative_poa_no", v || null)}
          />
        </div>

        {/* ── Property (العقار) ── */}
        <SectionLabel label={t("بيانات العقار", "Property Details")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReviewField
            label={t("رقم الصك", "Deed #")}
            fieldKey="deed_number"
            value={fields.deed_number ?? ""}
            edited={editedFields.has("deed_number")}
            onChange={(v) => handleFieldChange("deed_number", v || null)}
          />
          <ReviewField
            label={t("رقم تسجيل العقار", "Property Registration #")}
            fieldKey="property_registration_no"
            value={fields.property_registration_no ?? ""}
            edited={editedFields.has("property_registration_no")}
            onChange={(v) => handleFieldChange("property_registration_no", v || null)}
          />
          <ReviewField
            label={t("تاريخ الصك", "Deed Date")}
            fieldKey="deed_date"
            value={fields.deed_date ?? ""}
            edited={editedFields.has("deed_date")}
            onChange={(v) => handleFieldChange("deed_date", v || null)}
            type="date"
          />
          <ReviewField
            label={t("عنوان العقار", "Property Address")}
            fieldKey="property_address"
            value={fields.property_address ?? ""}
            edited={editedFields.has("property_address")}
            onChange={(v) => handleFieldChange("property_address", v || null)}
          />
          <ReviewField
            label={t("نوع العقار", "Property Type")}
            fieldKey="property_type"
            value={fields.property_type ?? ""}
            edited={editedFields.has("property_type")}
            onChange={(v) => handleFieldChange("property_type", v || null)}
          />
          <ReviewField
            label={t("استخدام العقار", "Property Use")}
            fieldKey="property_use"
            value={fields.property_use ?? ""}
            edited={editedFields.has("property_use")}
            onChange={(v) => handleFieldChange("property_use", v || null)}
          />
          <ReviewField
            label={t("رقم المخطط", "Plan #")}
            fieldKey="plan_no"
            value={fields.plan_no ?? ""}
            edited={editedFields.has("plan_no")}
            onChange={(v) => handleFieldChange("plan_no", v || null)}
          />
          <ReviewField
            label={t("رقم القطعة", "Plot #")}
            fieldKey="plot_no"
            value={fields.plot_no ?? ""}
            edited={editedFields.has("plot_no")}
            onChange={(v) => handleFieldChange("plot_no", v || null)}
          />
          <ReviewField
            label={t("المساحة (م²)", "Area (m²)")}
            fieldKey="property_area"
            value={fields.property_area?.toString() ?? ""}
            edited={editedFields.has("property_area")}
            onChange={(v) => handleFieldChange("property_area", v && !isNaN(Number(v)) ? Number(v) : null)}
            type="number"
          />
          <ReviewField
            label={t("أدنى سعر وحدة (⃁)", "Min Unit Sale Price (⃁)")}
            fieldKey="min_unit_sale_price"
            value={fields.min_unit_sale_price?.toString() ?? ""}
            edited={editedFields.has("min_unit_sale_price")}
            onChange={(v) => handleFieldChange("min_unit_sale_price", v && !isNaN(Number(v)) ? Number(v) : null)}
            type="number"
          />
          <ReviewField
            label={t("أدنى إيجار سنوي (⃁)", "Min Annual Rent (⃁)")}
            fieldKey="min_annual_rent"
            value={fields.min_annual_rent?.toString() ?? ""}
            edited={editedFields.has("min_annual_rent")}
            onChange={(v) => handleFieldChange("min_annual_rent", v && !isNaN(Number(v)) ? Number(v) : null)}
            type="number"
          />
          <ReviewField
            label={t("الخدمات", "Services")}
            fieldKey="property_services"
            value={fields.property_services ?? ""}
            edited={editedFields.has("property_services")}
            onChange={(v) => handleFieldChange("property_services", v || null)}
          />
        </div>

        {/* ── Commission ── */}
        <SectionLabel label={t("العمولة", "Commission")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReviewField
            label={t("نسبة عمولة المبيعات %", "Sales Commission %")}
            fieldKey="sales_commission_pct"
            value={fields.sales_commission_pct?.toString() ?? ""}
            edited={editedFields.has("sales_commission_pct")}
            onChange={(v) =>
              handleFieldChange("sales_commission_pct", v && !isNaN(Number(v)) ? Number(v) : null)
            }
            type="number"
          />
          <ReviewField
            label={t("نسبة عمولة التسويق %", "Marketing Commission %")}
            fieldKey="marketing_commission_pct"
            value={fields.marketing_commission_pct?.toString() ?? ""}
            edited={editedFields.has("marketing_commission_pct")}
            onChange={(v) =>
              handleFieldChange("marketing_commission_pct", v && !isNaN(Number(v)) ? Number(v) : null)
            }
            type="number"
          />
          <ReviewField
            label={t("طريقة الدفع", "Payment Method")}
            fieldKey="payment_method"
            value={fields.payment_method ?? ""}
            edited={editedFields.has("payment_method")}
            onChange={(v) => handleFieldChange("payment_method", v || null)}
          />
        </div>

        <div className="flex justify-between gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => setStep(mode === "manual" ? "choose" : "upload")}
          >
            {/* RTL icon fix: single icon + rtl:rotate-180 — no language conditional */}
            <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            {t("رجوع", "Back")}
          </Button>
          <Button onClick={() => setStep("link")}>
            {t("التالي", "Next")}
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </Button>
        </div>
      </div>
    );
  }

  // ── Step: link ────────────────────────────────────────────────────────────
  if (step === "link") {
    const ownerMissing = !selectedOwnerId;
    // AC2 guard — when specific-unit mode is chosen but no units have been
    // picked, advancing to Confirm would cause the linkProperty call to send
    // neither unit_ids nor all_units, defaulting the API to whole-property
    // coverage (the opposite of intent). Block Next until at least one unit
    // is selected or the user switches back to "all units".
    const unitScopeInvalid =
      !!selectedPropertyId && !allUnits && selectedUnitIds.length === 0;

    return (
      <div className="space-y-4">
        <StepsHeader current="link" mode={mode} />
        <p className="text-sm text-muted-foreground">
          {t(
            "نطابق المالك والعقار المستخرجين مع سجلات النظام تلقائياً — عدّل عند الحاجة أو أنشئ سجلاً جديداً.",
            "We auto-match the extracted owner & property to your records — adjust, or create a new record.",
          )}
        </p>

        {/* Owner — REQUIRED (auto-matched by national ID; create-if-missing) */}
        <BrokerageOwnerResolver
          extracted={fields}
          value={selectedOwnerId}
          onChange={setSelectedOwnerId}
          hasError={ownerMissing}
        />
        {ownerMissing && (
          <p className="text-xs text-destructive">
            {t("المالك مطلوب لإنشاء العقد", "Owner is required to create the contract")}
          </p>
        )}

        {/* Property — OPTIONAL (auto-matched by deed/RER; create needs the owner first) */}
        <BrokeragePropertyResolver
          extracted={fields}
          value={selectedPropertyId}
          onChange={(pid) => {
            setSelectedPropertyId(pid);
            // Reset unit scope when the property changes — unit ids belong to a
            // specific property and must not leak across a re-selection.
            setAllUnits(true);
            setSelectedUnitIds([]);
          }}
          ownerId={selectedOwnerId}
          allUnits={allUnits}
          onAllUnitsChange={(v) => {
            setAllUnits(v);
            if (v) setSelectedUnitIds([]);
          }}
          selectedUnitIds={selectedUnitIds}
          onUnitsChange={setSelectedUnitIds}
        />

        {/* Contract type — shown when a property is selected */}
        {selectedPropertyId && (
          <div className="space-y-2">
            <Label>{t("نوع العملية", "Operation Type")}</Label>
            <Select
              value={selectedContractType}
              onValueChange={(v) => setSelectedContractType(v as BrokerageOpsType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTRACT_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelAr, opt.labelEn)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          <Button variant="outline" onClick={() => setStep("review")}>
            <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            {t("رجوع", "Back")}
          </Button>
          <Button
            onClick={() => setStep("confirm")}
            disabled={ownerMissing || unitScopeInvalid}
            title={
              unitScopeInvalid
                ? t(
                    "اختر وحدة واحدة على الأقل أو اختر كل الوحدات",
                    "Select at least one unit, or switch to all units",
                  )
                : undefined
            }
          >
            {t("التالي", "Next")}
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </Button>
          {unitScopeInvalid && (
            <p className="text-xs text-destructive w-full text-end">
              {t(
                "يجب اختيار وحدة واحدة على الأقل أو تفعيل خيار كل الوحدات",
                "Select at least one unit or enable all-units coverage",
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Step: confirm ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <StepsHeader current="confirm" mode={mode} />

      <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
        <CardContent className="p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-1 text-sm">
            <p className="font-semibold text-amber-800 dark:text-amber-300">
              {mode === "manual"
                ? t("مراجعة البيانات المُدخلة", "Review Entered Data")
                : t("مراجعة البيانات المُستخرجة", "Review Extracted Data")}
            </p>
            <p className="text-amber-700 dark:text-amber-400">
              {mode === "manual"
                ? t(
                    "يرجى التحقق من صحة البيانات التي أدخلتها قبل الحفظ.",
                    "Please verify the data you entered before saving.",
                  )
                : t(
                    "يرجى التحقق من صحة البيانات المُستخرجة قبل الحفظ. البيانات المستخرجة آلياً قد تحتوي على أخطاء.",
                    "Please verify the accuracy of extracted data before saving. Auto-extracted data may contain errors.",
                  )}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3 text-sm">
        <SummaryRow label={t("رقم REGA", "REGA #")} value={fields.rega_contract_number} />
        <SummaryRow label={t("رقم الصك", "Deed #")} value={fields.deed_number} />
        <SummaryRow label={t("مدينة التوقيع", "Signing City")} value={fields.place_city} />
        <SummaryRow label={t("اسم الوسيط", "Broker")} value={fields.broker_name} />
        <SummaryRow label={t("رقم ترخيص الوسيط", "Broker License #")} value={fields.broker_license_number} />
        <SummaryRow label={t("اسم المالك", "Owner Name")} value={fields.owner_name_on_contract} />
        <SummaryRow label={t("رقم هوية المالك", "Owner ID #")} value={fields.owner_id_number} />
        <SummaryRow label={t("رقم تسجيل العقار", "Property Reg. #")} value={fields.property_registration_no} />
        <SummaryRow label={t("عنوان العقار", "Property Address")} value={fields.property_address} />
        <SummaryRow label={t("تاريخ البداية", "Start")} value={fields.start_date} />
        <SummaryRow label={t("تاريخ الانتهاء", "End")} value={fields.end_date} />
        {selectedOwnerId && (
          <SummaryRow
            label={t("المالك", "Owner")}
            value={owners.find((o) => o.id === selectedOwnerId)?.full_name}
          />
        )}
        {selectedPropertyId && (
          <>
            <SummaryRow
              label={t("العقار", "Property")}
              value={properties.find((p) => p.id === selectedPropertyId)?.name}
            />
            <SummaryRow
              label={t("نوع العملية", "Operation")}
              value={(() => {
                const opt = CONTRACT_TYPE_OPTIONS.find(
                  (o) => o.value === selectedContractType,
                );
                return opt ? t(opt.labelAr, opt.labelEn) : undefined;
              })()}
            />
            <SummaryRow
              label={t("نطاق العقد", "Scope")}
              value={
                allUnits
                  ? t("كل الوحدات (العقار بالكامل)", "All units (whole property)")
                  : t(
                      `${selectedUnitIds.length} وحدة محددة`,
                      `${selectedUnitIds.length} specific unit(s)`,
                    )
              }
            />
          </>
        )}
        {editedFields.size > 0 && (
          <p className="text-xs text-muted-foreground">
            {t(
              `تم تعديل ${editedFields.size} حقل يدوياً`,
              `${editedFields.size} field(s) manually edited`,
            )}
          </p>
        )}
      </div>

      {/* Optional free-text note saved with the contract */}
      <div className="space-y-2">
        <Label htmlFor="brokerage-notes">
          {t("ملاحظات", "Notes")}{" "}
          <span className="text-muted-foreground text-xs">({t("اختياري", "optional")})</span>
        </Label>
        <Textarea
          id="brokerage-notes"
          rows={3}
          value={fields.notes ?? ""}
          // notes is a manual free-text field — extraction never populates it today,
          // so it intentionally skips handleFieldChange/editedFields tracking. If a
          // future increment extracts `notes` from the PDF (it's in
          // EXTRACTED_BROKERAGE_FIELD_KEYS), switch to handleFieldChange("notes", …)
          // so user edits surface in manually_edited_fields.
          onChange={(e) => setFields((f) => ({ ...f, notes: e.target.value || null }))}
          placeholder={t("أي ملاحظات إضافية عن هذا العقد…", "Any extra notes about this contract…")}
        />
      </div>

      <div className="flex items-start gap-3 rounded-lg border p-4 bg-muted/30">
        <Checkbox
          id="acknowledge"
          checked={acknowledged}
          onCheckedChange={(v) => setAcknowledged(Boolean(v))}
        />
        <label htmlFor="acknowledge" className="text-sm leading-relaxed cursor-pointer">
          {mode === "manual"
            ? t(
                "أؤكد صحة بيانات العقد التي أدخلتها",
                "I confirm the contract data I entered is accurate",
              )
            : t(
                "أتحقق من صحة البيانات المُستخرجة وأؤكد مطابقتها للعقد الورقي",
                "I have verified the extracted fields match the original contract document",
              )}
        </label>
      </div>

      <div className="flex justify-between gap-2 pt-2">
        <Button variant="outline" onClick={() => setStep("link")}>
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
          {t("رجوع", "Back")}
        </Button>
        <Button onClick={handleSubmit} disabled={!acknowledged || isSubmitting}>
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin me-2" />}
          {t("حفظ العقد", "Save Contract")}
        </Button>
      </div>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function StepsHeader({
  current,
  mode,
}: {
  current: "upload" | "review" | "link" | "confirm";
  mode: EntryMode;
}) {
  const { t } = useLanguage();
  // Manual entry has no Upload step, so the indicator renders 3 steps instead of 4.
  const steps: { key: "upload" | "review" | "link" | "confirm"; label: string }[] =
    mode === "manual"
      ? [
          { key: "review", label: t("إدخال البيانات", "Enter Data") },
          { key: "link", label: t("ربط", "Link") },
          { key: "confirm", label: t("تأكيد", "Confirm") },
        ]
      : [
          { key: "upload", label: t("رفع الملف", "Upload") },
          { key: "review", label: t("مراجعة البيانات", "Review") },
          { key: "link", label: t("ربط", "Link") },
          { key: "confirm", label: t("تأكيد", "Confirm") },
        ];
  // Math.max guards the (unreachable) case where `current` isn't in this mode's
  // step list — findIndex would return -1 and render every step as "future".
  const currentIndex = Math.max(0, steps.findIndex((s) => s.key === current));
  return (
    <div className="flex items-center gap-2 mb-2">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1">
          <StepIndicator step={i + 1} current={currentIndex + 1} />
          <span
            className={cn(
              "hidden sm:inline text-xs",
              i === currentIndex
                ? "font-semibold text-primary"
                : "text-muted-foreground",
            )}
          >
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <div className="h-px w-6 bg-muted mx-1 hidden sm:block" />
          )}
        </div>
      ))}
    </div>
  );
}

/** Big clickable card used by the entry chooser (upload vs manual). */
function ModeCard({
  icon,
  title,
  description,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-3 rounded-xl border-2 border-muted p-6 text-center transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="text-primary">{icon}</span>
      <span className="font-semibold">{title}</span>
      <span className="text-sm text-muted-foreground">{description}</span>
    </button>
  );
}

/** Thin section-divider label used in the Review step to group field cards. */
function SectionLabel({ label }: { label: string }) {
  return (
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b pb-1">
      {label}
    </p>
  );
}

function ReviewField({
  label,
  fieldKey,
  value,
  edited,
  onChange,
  type = "text",
}: {
  label: string;
  fieldKey: string;
  value: string;
  edited: boolean;
  onChange: (v: string) => void;
  type?: "text" | "date" | "number" | "phone";
}) {
  const { language } = useLanguage();
  if (type === "phone") {
    // Extracted values arrive in any shape; show them in the shared E.164 phone box. A +966 prefix means the
    // user is mid-typing, so it passes through untouched.
    const e164 = value.startsWith("+966") ? value : toE164Phone(value);
    return (
      <div className="space-y-1">
        <Label className="text-xs flex items-center gap-1">
          {label}
          {value && !edited && (
            <span className="text-xs text-primary font-normal">✦</span>
          )}
        </Label>
        <PhoneInput
          value={e164}
          onChange={onChange}
          className={cn(
            edited && "[&_input]:border-amber-400",
            value && !edited && "[&_input]:border-primary/40",
          )}
          data-testid={fieldKey}
        />
      </div>
    );
  }
  if (type === "date") {
    // Gov-native contract date → shared picker with the Umm al-Qura Hijri helper.
    return (
      <div className="space-y-1">
        <Label className="text-xs flex items-center gap-1">
          {label}
          {value && !edited && (
            <span className="text-xs text-primary font-normal">✦</span>
          )}
        </Label>
        <DatePicker
          value={value}
          onChange={onChange}
          locale={language === "ar" ? "ar" : "en"}
          showHijri
          dataField={fieldKey}
          className={cn(
            edited && "border-amber-400 focus-visible:ring-amber-400",
            value && !edited && "border-primary/40",
          )}
        />
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs flex items-center gap-1">
        {label}
        {value && !edited && (
          <span className="text-xs text-primary font-normal">✦</span>
        )}
      </Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type={type}
        dir={type === "text" && /[؀-ۿ]/.test(value) ? "rtl" : "ltr"}
        className={cn(
          edited && "border-amber-400 focus-visible:ring-amber-400",
          value && !edited && "border-primary/40",
        )}
        data-field={fieldKey}
      />
    </div>
  );
}

function SummaryRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
